'use client';

import React from 'react';
import Image from 'next/image';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '@/components/ui/carousel';
import { ArrowRight } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { getBranchCodeFromPath, regionalizePath } from '@/lib/config/branches-config';

interface PortfolioProjectsProps {
  projects?: any[];
  title?: string;
  description?: string;
}

export default function PortfolioProjects({
  projects = [],
  title = 'Примеры выполненных работ',
  description = 'Реализованные проекты по строительству и производству металлоконструкций',
}: PortfolioProjectsProps) {
  const pathname = usePathname() || '/';
  const currentBranchCode = getBranchCodeFromPath(pathname);
  return (
    <div className="bg-white py-3 sm:py-4 md:py-6 lg:py-8 w-full max-w-full overflow-hidden">
      <div className="">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 md:gap-6">
          <div className="flex-1 min-w-0">
            <h2 className="mt-2 max-w-xl text-lg font-semibold tracking-tight text-pretty text-gray-950 sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl">
              {title}
            </h2>
            <p className="mt-1.5 sm:mt-2 text-xs/6 sm:text-sm/7 md:text-base/8 lg:text-lg/8 text-gray-600 max-w-3xl">
              {description}
            </p>
          </div>
          <a href={regionalizePath("/portfolio", currentBranchCode)} className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap rounded-md text-xs sm:text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-blue-950 text-blue-950 hover:bg-blue-950 hover:text-white h-8 sm:h-9 md:h-10 px-3 sm:px-4 py-1.5 sm:py-2">
            <span className="hidden sm:inline">Показать все работы</span>
            <span className="sm:hidden">Все работы</span>
            <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
          </a>
        </div>
        <div className="mt-4 sm:mt-6 md:mt-8 relative w-full max-w-full grid">
          <div className="w-full max-w-full overflow-hidden">
            <Carousel
              opts={{
                align: 'start',
                loop: false,
                dragFree: true,
                containScroll: 'trimSnaps',
                skipSnaps: false,
              }}
              className="w-full max-w-full"
            >
              <CarouselContent className="!ml-0 gap-[var(--slide-gap)] [--slide-gap:12px] sm:[--slide-gap:16px] lg:[--slide-gap:20px] xl:[--slide-gap:24px]">
                {projects.length > 0 ? (
                  projects.map((project, index) => {
                    // Обрабатываем разные форматы данных
                    const projectId = project.ID || project.id || project.slug || `project-${index}`;
                    const projectTitle = project.NAME || project.title || project.name || '';
                    const projectImage = project.imageUrl || project.PREVIEW_PICTURE || project.PREVIEW_PICTURE_URL || project.image;
                    const projectSlug = project.slug || project.CODE || project.code || projectId;
                    
                    return (
                      <CarouselItem
                        key={`project-${projectId}-${index}`}
                        className="!pl-0 !basis-[90%] sm:!basis-[70%] md:!basis-[calc((100%_-_var(--slide-gap))/_2)] lg:!basis-[calc((100%_-_2*var(--slide-gap))/_3)] xl:!basis-[calc((100%_-_3*var(--slide-gap))/_4)]"
                      >
                        <a href={regionalizePath(`/portfolio/${projectSlug}`, currentBranchCode)} className="block h-full">
                          <article className="flex h-full flex-col items-start justify-between group">
                            <div className="relative w-full">
                              {projectImage ? (
                                <Image
                                  alt={projectTitle}
                                  src={projectImage}
                                  width={800}
                                  height={600}
                                  className="aspect-video w-full rounded-lg sm:rounded-xl md:rounded-2xl bg-gray-100 object-cover sm:aspect-2/1 md:aspect-3/2"
                                />
                              ) : (
                                <div className="aspect-video w-full rounded-lg sm:rounded-xl md:rounded-2xl bg-gray-200 flex items-center justify-center">
                                  <span className="text-gray-400 text-[10px] sm:text-xs">Нет изображения</span>
                                </div>
                              )}
                              <div className="absolute inset-0 rounded-lg sm:rounded-xl md:rounded-2xl inset-ring inset-ring-gray-900/10" />
                            </div>
                            <div className="flex max-w-xl grow flex-col justify-between w-full">
                              <div className="relative grow w-full">
                                <h3 className="mt-2 sm:mt-2.5 md:mt-3 text-sm font-semibold text-gray-900 group-hover:text-gray-600 line-clamp-2 sm:line-clamp-3">
                                  {projectTitle}
                                </h3>
                              </div>
                            </div>
                          </article>
                        </a>
                      </CarouselItem>
                    );
                  })
                ) : (
                  <CarouselItem className="!pl-0 !basis-[90%] sm:!basis-[70%] md:!basis-[calc((100%_-_var(--slide-gap))/_2)] lg:!basis-[calc((100%_-_2*var(--slide-gap))/_3)] xl:!basis-[calc((100%_-_3*var(--slide-gap))/_4)]">
                    <div className="text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">Проекты временно недоступны</div>
                  </CarouselItem>
                )}
              </CarouselContent>
              <CarouselPrevious className="hidden xl:flex -left-6" />
              <CarouselNext className="hidden xl:flex -right-6" />
            </Carousel>
          </div>
        </div>
      </div>
    </div>
  );
}
